import type {
  KnowledgeRelation,
  KnowledgeStats,
  KnowledgeUnit,
  RelationType,
} from "../../api/knowledge/knowledge";

/** `MIN_GROUP_MEMBERS` de tc_learn `content/knowledge.py`. */
const MIN_GROUP_MEMBERS = 2;
/** `MIN_CATEGORIES` de tc_learn `tools/category_sort/guards.py`. */
const MIN_CATEGORIES = 2;
/** `MIN_PAIRS` de tc_learn `tools/pair_matching/spec.py`. */
const MIN_PAIRS = 3;

/**
 * Éligibilité des formes d'exercice, lue depuis les traits de l'unité.
 *
 * Ce qui fait foi, c'est tc_learn `main` — on recopie ses seuils ici pour que
 * l'équipe qui importe voie immédiatement ce que sa correction change, sans
 * attendre une génération. Une base pauvre rend la forme inéligible, jamais
 * un faux exercice. Fichier par fichier :
 *  - les compteurs : `tc_learn/content/knowledge.py` (`traits`,
 *    `functional_relations`, `declared_groups`, `computed_groups_by_label`) ;
 *  - les seuils : le `trait_gate` ou le `Builder.traits_ok` de chaque tool,
 *    `tc_learn/tools/<forme>/` (les formes historiques sous `tools/_legacy/`) ;
 *  - l'analogie : `tc_learn/content/builders/analogy.py`, parce que son
 *    pré-filtre (≥ 2 triplets) laisse passer des unités dont le constructeur
 *    ne tire jamais deux leurres — l'afficher promettrait un exercice jamais
 *    servi ;
 *  - ce qui est servi : `templates_disabled` de `tc_learn/config.py`.
 * Le constructeur garde le dernier mot (membres ambigus, définitions en
 * double) : « possible » ici veut dire « les seuils sont atteints ».
 */

/** Traits mesurés : les stats du serveur + ce que le contrat n'agrège pas. */
export interface KnowledgeTraits extends KnowledgeStats {
  /** Termes actifs dont la définition n'est pas vide (`n_defined_terms`). */
  defined_terms?: number;
  /**
   * Catégories exploitables : ≥ 2 membres qui désignent un terme actif
   * (`n_declared_groups`). `categories` les compte toutes, celles d'un seul
   * membre comprises.
   */
  declared_groups?: number;
  /**
   * Groupes CALCULÉS du meilleur libellé : arrivées partagées par ≥ 2 départs
   * distincts (`n_computed_groups`). Le maximum par libellé, parce qu'un tri
   * se joue sur un seul critère.
   */
  computed_groups?: number;
  /**
   * Idées fausses relationnelles qui peuvent servir de leurre à l'analogie,
   * par libellé normalisé : même libellé qu'un triplet fonctionnel jouable,
   * partant d'un de ses `from` (le C de l'analogie), vers un terme actif.
   * C'est le seul leurre qui complète une analogie à 3 triplets.
   */
  analogy_misconceptions?: Record<string, number>;
}

export type KnowledgeFormKey =
  | "concept_map"
  | "analogy"
  | "timeline"
  | "step_builder"
  | "category_sort"
  | "pair_matching";

export interface KnowledgeForm {
  key: KnowledgeFormKey;
  label: string;
  possible: boolean;
  /** Ce que la forme exige, affiché tel quel quand elle est impossible. */
  requirement: string;
  /**
   * `false` = tc_learn ne sert pas cette forme, quelle que soit la base. On
   * l'affiche barrée plutôt que de promettre un exercice qui n'existe pas.
   */
  served: boolean;
}

/**
 * Normalisation du libellé d'une relation : minuscules, espaces repliés,
 * apostrophes `'`/`’` unifiées. Même règle que `NormalizeFactText` (Go) et
 * `normalize_label` (tc_learn) : deux voisins écrivent « s’oxyde en » et
 * « s'oxyde en », et sans ça le groupe fonctionnel se coupe en deux.
 */
export const normalizeLabel = (label: string): string =>
  (label ?? "")
    .replace(/[’‘`´]/g, "'")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** `n_asym` de tc_learn : les relations dont un sens faux est garanti. */
export const countAsymmetric = (stats: KnowledgeStats): number =>
  (stats.relations?.asymmetric ?? 0) + (stats.relations?.functional ?? 0);

/**
 * Fonctionnalité CALCULÉE, jamais déclarée seule (annexe A + critique §4/§6).
 *
 * Une relation compte pour un triplet fonctionnel si, et seulement si :
 *  1. son libellé normalisé a été déclaré `functional` (`declared_type`) par au
 *     moins une relation active de l'unité — sans quoi « accélère la corrosion
 *     de » passerait pour fonctionnel alors que l'eau accélère la corrosion du
 *     fer ET du cuivre, et la substitution servirait un leurre VRAI ;
 *  2. son type EFFECTIF est `functional` ou `asymmetric` — jamais `symmetric`,
 *     jamais `undeclared` (aucun sens n'y est garanti faux) ;
 *  3. son `from` n'a qu'un seul `to` pour ce libellé parmi les relations
 *     ACTIVES : une seconde arrivée, même vers un terme inconnu, retire la
 *     fonctionnalité.
 *
 * Ne comptent ensuite que les triplets JOUABLES, comme `traits()` de
 * tc_learn : entre deux termes actifs, sans boucle, et une seule fois par
 * couple (from, to) — un fait recopié deux fois par l'extraction ne fait pas
 * deux triplets. L'unicité, elle, se constate sur TOUTES les relations
 * actives : une seconde arrivée vers un terme inconnu suffit à la retirer.
 *
 * Retourne le nombre de triplets par libellé normalisé — la même clé que
 * `stats.functional_labels` du serveur, que tc_learn recalcule de son côté.
 */
export const computeFunctionalLabels = (
  relations: readonly KnowledgeRelation[],
  termIds: ReadonlySet<string>,
): Record<string, number> => {
  const declared = new Set<string>();
  relations.forEach((relation) => {
    if (relation.declared_type === "functional") declared.add(normalizeLabel(relation.label));
  });

  // Arrivées distinctes par (from, libellé) : c'est l'unicité qu'on constate.
  const targets = new Map<string, Set<string>>();
  relations.forEach((relation) => {
    const key = `${relation.from}\u0000${normalizeLabel(relation.label)}`;
    const set = targets.get(key) ?? new Set<string>();
    set.add(relation.to);
    targets.set(key, set);
  });

  const pairs = new Map<string, Set<string>>();
  relations.forEach((relation) => {
    const label = normalizeLabel(relation.label);
    if (!declared.has(label)) return;
    if (relation.type !== "functional" && relation.type !== "asymmetric") return;
    if ((targets.get(`${relation.from}\u0000${label}`)?.size ?? 0) !== 1) return;
    if (relation.from === relation.to) return;
    if (!termIds.has(relation.from) || !termIds.has(relation.to)) return;
    const set = pairs.get(label) ?? new Set<string>();
    set.add(`${relation.from}\u0000${relation.to}`);
    pairs.set(label, set);
  });

  const counts: Record<string, number> = {};
  pairs.forEach((set, label) => {
    counts[label] = set.size;
  });
  return counts;
};

/**
 * `computed_groups_by_label` de tc_learn, réduit au compte qui décide : pour
 * chaque libellé, les arrivées atteintes par ≥ 2 départs distincts ; on rend
 * le maximum sur les libellés. Toutes les relations actives comptent, quel
 * que soit leur type — on ne déduit rien, le fait tel qu'il est écrit suffit.
 */
export const computeComputedGroups = (
  relations: readonly KnowledgeRelation[],
  termIds: ReadonlySet<string>,
): number => {
  const froms = new Map<string, Map<string, Set<string>>>();
  relations.forEach((relation) => {
    if (relation.from === relation.to) return;
    if (!termIds.has(relation.from) || !termIds.has(relation.to)) return;
    const label = normalizeLabel(relation.label);
    if (!label) return;
    const byTarget = froms.get(label) ?? new Map<string, Set<string>>();
    const set = byTarget.get(relation.to) ?? new Set<string>();
    set.add(relation.from);
    byTarget.set(relation.to, set);
    froms.set(label, byTarget);
  });
  let best = 0;
  froms.forEach((byTarget) => {
    let groups = 0;
    byTarget.forEach((set) => {
      if (set.size >= MIN_GROUP_MEMBERS) groups += 1;
    });
    best = Math.max(best, groups);
  });
  return best;
};

export const possibleForms = (stats: KnowledgeTraits): KnowledgeForm[] => {
  const asym = countAsymmetric(stats);
  const functional = stats.functional_labels ?? {};
  const decoys = stats.analogy_misconceptions ?? {};
  // A:B::C:? montre deux triplets et il faut ≥ 2 leurres (`MIN_DISTRACTORS`
  // de builders/analogy.py) : les autres arrivées du libellé, ou une idée
  // fausse portant sur le C. D'où 4 triplets, ou 3 + une idée fausse.
  const analogyReady = Object.entries(functional).some(
    ([label, n]) => n >= 4 || (n >= 3 && (decoys[label] ?? 0) >= 1),
  );

  return [
    {
      /* Seuil de `tools/_legacy/concept_map.py:_builder_traits_ok`. Le
         gabarit `concept_map_v2` est dans `templates_disabled` par défaut
         (config.py) depuis la campagne UAT du 11/10/2026 — cartes fausses
         7 fois sur 9 : la base peut la permettre, le moteur ne la sert pas. */
      key: "concept_map",
      label: "Carte de concepts",
      possible: false,
      requirement:
        "≥ 4 termes et ≥ 3 relations asymétriques ou fonctionnelles " +
        `(la base en a ${stats.terms ?? 0} et ${asym}) — ` +
        "forme retirée du moteur jusqu'à relecture de la construction des arêtes",
      served: false,
    },
    {
      key: "analogy",
      label: "Analogie",
      possible: analogyReady,
      requirement:
        "un libellé fonctionnel calculé portant ≥ 4 triplets " +
        "(ou 3 + une idée fausse sur ce libellé)",
      served: true,
    },
    {
      // `tools/_legacy/timeline_builder.py:trait_gate`.
      key: "timeline",
      label: "Frise",
      possible: (stats.dates ?? 0) >= 3,
      requirement: "≥ 3 dates distinctes",
      served: true,
    },
    {
      // `tools/step_builder/__init__.py:trait_gate`.
      key: "step_builder",
      label: "Remise en ordre",
      possible: (stats.steps ?? 0) >= 4,
      requirement: "≥ 4 étapes",
      served: true,
    },
    {
      // `tools/category_sort/build.py:traits_ok` (`MIN_CATEGORIES` = 2).
      key: "category_sort",
      label: "Tri en catégories",
      possible:
        (stats.declared_groups ?? 0) >= MIN_CATEGORIES ||
        (stats.computed_groups ?? 0) >= MIN_CATEGORIES,
      requirement:
        "≥ 2 catégories d'au moins 2 termes, ou ≥ 2 arrivées partagées " +
        "par ≥ 2 termes sous un même libellé de relation",
      served: true,
    },
    {
      // `tools/pair_matching/build.py:traits_ok` (`MIN_PAIRS` = 3).
      key: "pair_matching",
      label: "Appariement",
      possible: (stats.defined_terms ?? 0) >= MIN_PAIRS,
      requirement: "≥ 3 termes définis",
      served: true,
    },
  ];
};

/**
 * Traits recalculés sur les faits ACTIFS.
 *
 * Les `stats` de l'API décrivent l'unité au chargement ; dès qu'un éditeur
 * bascule un fait, les formes possibles doivent suivre sans aller-retour.
 */
export const computeTraits = (
  unit: KnowledgeUnit,
  disabled: ReadonlySet<string>,
): KnowledgeTraits => {
  const active = <T extends { id: string }>(items: T[] | undefined): T[] =>
    (items ?? []).filter((item) => !disabled.has(item.id));

  const terms = active(unit?.terms);
  const activeTermIds = new Set(terms.map((term) => term.id));
  const relations = active(unit?.relations);
  const byType: Partial<Record<RelationType, number>> = {};
  relations.forEach((relation) => {
    byType[relation.type] = (byType[relation.type] ?? 0) + 1;
  });
  const functionalLabels = computeFunctionalLabels(relations, activeTermIds);

  // Le C d'une analogie est le `from` d'un triplet fonctionnel jouable.
  const functionalFroms = new Map<string, Set<string>>();
  relations.forEach((relation) => {
    const label = normalizeLabel(relation.label);
    if (!(label in functionalLabels)) return;
    const set = functionalFroms.get(label) ?? new Set<string>();
    set.add(relation.from);
    functionalFroms.set(label, set);
  });

  const misconceptions = active(unit?.misconceptions);
  const analogyMisconceptions: Record<string, number> = {};
  misconceptions.forEach((item) => {
    if (!item.label || !item.from || !item.to) return;
    const label = normalizeLabel(item.label);
    if (!functionalFroms.get(label)?.has(item.from)) return;
    if (!activeTermIds.has(item.to)) return;
    analogyMisconceptions[label] = (analogyMisconceptions[label] ?? 0) + 1;
  });

  const categories = active(unit?.categories);
  // Un membre désactivé ne compte pas : le tri se joue sur des termes servis,
  // et un membre cité deux fois n'est qu'un élément.
  const declaredGroups = categories.filter(
    (category) =>
      new Set((category.members ?? []).filter((id) => activeTermIds.has(id))).size >=
      MIN_GROUP_MEMBERS,
  ).length;

  return {
    terms: activeTermIds.size,
    defined_terms: terms.filter((term) => (term.definition ?? "").trim() !== "").length,
    relations: {
      ...byType,
      functional_computed: Object.values(functionalLabels).reduce((sum, n) => sum + n, 0),
    },
    functional_labels: functionalLabels,
    steps: active(unit?.steps).length,
    // Les dates distinctes : deux faits sur la même date ne font pas une frise.
    dates: new Set(active(unit?.dates).map((date) => date.when)).size,
    misconceptions: misconceptions.length,
    analogy_misconceptions: analogyMisconceptions,
    categories: categories.length,
    declared_groups: declaredGroups,
    computed_groups: computeComputedGroups(relations, activeTermIds),
  };
};
